import { RESULTS_LIMIT_ASSET_SCAN } from "@/constants/resultsLimits";
import { ScanAsset, ScanEvent, ScanEventType, Stored } from "@/models/assetScan/AssetScanTypes";
import { recomputeAssetState } from "@/models/assetScan/recomputeAssetState";
import { whereEquals } from "@/models/assetScan/whereClause";
import { ADDIN_DATA_MAX_CHARS, AddInDataTooLargeError } from "@/utilities/addInData/upsertAddInData";

export type GeotabCall = <T>(method: string, params: Record<string, unknown>, signal?: AbortSignal) => Promise<T>;

interface RawRow {
  id: string;
  details: unknown;
}

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

const COMPANY_GROUP = "GroupCompanyId";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Geotab keeps a field a Set leaves out, so `null` stands for "removed"; reading drops it again. */
function stripNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripNulls);
  if (!isObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [key, inner] of Object.entries(value)) {
    if (inner !== null && inner !== undefined) out[key] = stripNulls(inner);
  }
  return out;
}

/**
 * The details to Set: `next`, plus `null` for every field `previous` had and `next` dropped.
 * Geotab merges a Set into the stored object, so a dropped field would otherwise stay (live in
 * fairfleet_t1, 2026-11-04: a released customer stayed on the asset).
 */
export function nullDropped(previous: unknown, next: unknown): unknown {
  if (!isObject(previous) || !isObject(next)) return next === undefined ? null : next;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(next)) {
    if (value !== undefined) out[key] = nullDropped(previous[key], value);
  }
  for (const key of Object.keys(previous)) {
    if (!(key in out)) out[key] = null;
  }
  return out;
}

function checkedDetails(details: unknown): Json {
  const plain = JSON.parse(JSON.stringify(details)) as Json;
  const length = JSON.stringify(plain).length;
  if (length > ADDIN_DATA_MAX_CHARS) throw new AddInDataTooLargeError(length);
  return plain;
}

const groupsOf = (groupIds?: string[]) =>
  (groupIds && groupIds.length > 0 ? groupIds : [COMPANY_GROUP]).map(id => ({ id }));

const toStored = <T>(rows: RawRow[]): Stored<T>[] =>
  rows.map(row => ({ id: row.id, details: stripNulls(row.details) as T }));

export async function listAll<T>(call: GeotabCall, addInId: string, signal?: AbortSignal): Promise<Stored<T>[]> {
  const rows = await call<RawRow[]>(
    "Get",
    { typeName: "AddInData", search: { addInId }, resultsLimit: RESULTS_LIMIT_ASSET_SCAN },
    signal,
  );
  return toStored<T>(rows ?? []);
}

/** Rows whose `field` equals `value`. Without a selectClause Geotab returns whole rows (live check). */
export async function findBy<T>(
  call: GeotabCall,
  addInId: string,
  field: string,
  value: string,
  signal?: AbortSignal,
): Promise<Stored<T>[]> {
  const rows = await call<RawRow[]>(
    "Get",
    {
      typeName: "AddInData",
      search: { addInId, whereClause: whereEquals(field, value) },
      resultsLimit: RESULTS_LIMIT_ASSET_SCAN,
    },
    signal,
  );
  return toStored<T>(rows ?? []);
}

export async function addItem<T>(call: GeotabCall, addInId: string, details: T, groupIds?: string[]): Promise<string> {
  return call<string>("Add", {
    typeName: "AddInData",
    entity: { addInId, details: checkedDetails(details), groups: groupsOf(groupIds) },
  });
}

export async function setItem<T>(
  call: GeotabCall,
  addInId: string,
  id: string,
  details: T,
  previous: T,
  groupIds?: string[],
): Promise<void> {
  await call<null>("Set", {
    typeName: "AddInData",
    entity: { id, addInId, details: checkedDetails(nullDropped(previous, details)), groups: groupsOf(groupIds) },
  });
}

export async function removeItem(call: GeotabCall, addInId: string, id: string): Promise<void> {
  await call<null>("Remove", { typeName: "AddInData", entity: { id, addInId } });
}

export interface RecordEventArgs {
  addInIds: { assets: string; events: string };
  event: ScanEvent;
  asset: Stored<ScanAsset>;
  /** The asset's events before this one. */
  history: ScanEvent[];
  types: ScanEventType[];
}

/** One scan: the event first (it is the truth), then the asset's state rebuilt from the whole history. */
export async function recordEvent(call: GeotabCall, { addInIds, event, asset, history, types }: RecordEventArgs) {
  const eventId = await addItem(call, addInIds.events, event, asset.details.groupIds);
  await updateAssetState(call, addInIds.assets, asset, [...history, event], types);
  return eventId;
}

export async function updateAssetState(
  call: GeotabCall,
  assetsAddInId: string,
  asset: Stored<ScanAsset>,
  events: ScanEvent[],
  types: ScanEventType[],
): Promise<void> {
  const next: ScanAsset = { ...asset.details, state: recomputeAssetState(events, types) };
  await setItem(call, assetsAddInId, asset.id, next, asset.details, asset.details.groupIds);
}
